import React, { useEffect, useState } from 'react';
import MuiDrawer, { DrawerProps } from '@material-ui/core/Drawer';
import { useSelector, useDispatch } from 'react-redux';

import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { Scrollbars } from 'react-custom-scrollbars';
import styled, { css } from 'styled-components';

import { H2 } from '~/components';
import { RootState } from '~/types';
import { openDrawer, closeDrawer } from '~/ducks/ui';
import { Typography } from '@material-ui/core';
import { EditMode, useEditModeStyles } from '~/hooks/useEditModeContext';

interface StyledDrawerProps extends DrawerProps {
  $noBackgroundShadow?: boolean;
}

const StyledDrawer = styled(MuiDrawer)<StyledDrawerProps>`
  ${(props) =>
    props.$noBackgroundShadow &&
    css`
      .MuiBackdrop-root {
        background-color: transparent;
      }
    `}
`;

const useStyles = makeStyles((theme) => ({
  MuiDrawer: {
    overflowY: 'unset',
  },
  root: {
    width: 395,
    maxWidth: 395,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(1),
    '& > button': {
      padding: 3,
      marginTop: -3,
    },
  },
  content: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  noShadow: {
    '&:first-child': {
      backgroundColor: 'transparent',
    },
  },
  bottomSpacing: {
    marginBottom: theme.spacing(2),
  },
  wrap: {
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
}));

interface Props {
  heading: string | JSX.Element;
  subtitle?: string | JSX.Element;
  onClose: () => void;
  open: boolean;
  children: any;
  editMode?: EditMode; // ? move to useEditMode
}

const Counter = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(openDrawer());
    return () => {
      dispatch(closeDrawer());
    };
  }, [dispatch]);
  return null;
};

const Drawer = (props: Props) => {
  const classes = useStyles();
  const { heading, onClose, open, children, editMode, subtitle } = props;

  const editModeClasses = useEditModeStyles({ editMode: editMode ?? 'VIEW' });

  const openDrawersCount = useSelector<RootState, number>((state) => state.ui.openDrawers);
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (count === null && open) {
      setCount(openDrawersCount);
    }
  }, [openDrawersCount, count, open]);

  return (
    <StyledDrawer
      anchor='right'
      open={open}
      onClose={onClose}
      classes={{ paper: classes.MuiDrawer }}
      $noBackgroundShadow={Boolean(count && count > 0)}
    >
      {open && <Counter />}
      <Scrollbars
        className={editModeClasses.backgroundColor}
        style={{ width: 395, height: '100vh' }}
      >
        <div className={classes.root}>
          <div className={classes.header}>
            <div className={classes.bottomSpacing}>
              <H2 topSpacing={0} bottomSpacing={0} className={classes.wrap}>
                {heading}
              </H2>
              {typeof subtitle === 'string' ? (
                <Typography variant='subtitle2'>{subtitle}</Typography>
              ) : subtitle ? (
                subtitle
              ) : null}
            </div>
            <IconButton aria-label='Close' onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.content}>{children}</div>
        </div>
      </Scrollbars>
    </StyledDrawer>
  );
};

export default Drawer;
